import {
	Paper, Stack, Title, Button, Group, FileInput, Image, Anchor, Badge, CSSObject
} from '@mantine/core';
import Icon from '@ui/components/Icon/Icon';
import Text from '@ui/components/Text/Text';
import { textVariants } from '@ui/tokens/typography/typography';
import Workspace from 'src/entities/workspace';
import PricingPlanFeature from 'src/entities/pricing/pricingPlanFeature';
import FeaturePaywall from '@workspaces/components/FeaturePaywall/FeaturePaywall';
import useEditWorkspaceLogo from './useEditWorkspaceLogo';

const BADGE_STYLES = {
	root: {
		border: 0
	},
	inner: {
		...( textVariants[ 'caption-semibold' ] as CSSObject )
	}
};

const LogoPositionItem = ( { label, color }: { label: string; color: string; } ) => (
	<Group spacing={8}>
		<Icon icon="check_circle_outline" size={18} color={color} />
		<Text color={color} variant="body">{label}</Text>
	</Group>
);

const EditWorkspaceLogo = ( {
	workspace, readOnly
}: { workspace: Workspace; readOnly: boolean; } ) => {
	const {
		placeholder,
		onFileChange,
		logo,
		preview,
		saveButtonDisabled,
		fileInputDisabled,
		onSaveClick,
		isSaving,
		onRestoreDefaultClick,
		restoreDefaultButtonDisabled,
		isDeletingLogo,
		isFeatureAvailable,
		onCancelClick,
		showCancelButton
	} = useEditWorkspaceLogo( { workspace, readOnly } );

	const textColor = isFeatureAvailable ? 'black' : 'dark.3';

	return (
		<Paper p={20} withBorder shadow="xs">
			<Stack spacing={20} align="stretch">
				<Stack spacing={14}>
					<Group spacing={14}>
						<Title color={textColor} order={4}>Workspace Logo</Title>
						<Anchor
							h="18px"
							href="https://support.verybusy.io/en/articles/12710113-custom-branding-for-workspaces"
							target="_blank"
						>
							<Icon icon="info_outlined" size={18} color="dark.9" />
						</Anchor>
						<FeaturePaywall
							feature={PricingPlanFeature.WORKSPACE_CUSTOM_BRANDING}
							workspace={workspace}
						>
							{( { planName } ) => (
								<Badge color="green" size="xs" variant="light" styles={BADGE_STYLES}>
									{`${planName} Only`}
								</Badge>
							)}
						</FeaturePaywall>
					</Group>
					<Text color={textColor}>For best contrast, use a white version.</Text>
				</Stack>
				<FileInput
					icon={<Icon icon="upload" size={18} />}
					value={logo}
					onChange={onFileChange}
					accept="image/png,image/svg+xml"
					disabled={fileInputDisabled}
					placeholder={placeholder}
				/>
				{( preview ) && (
					<Stack h={72} align="center" justify="center" bg="blue.9" style={{ borderRadius: '4px' }}>
						<Image src={preview} alt="Workspace Logo" width="213px" height="49px" fit="contain" />
					</Stack>
				)}
				<Stack>
					<Text color={textColor} variant="body-bold">Where will this logo be applied?</Text>
					<Group spacing={40}>
						<Stack spacing={10}>
							<LogoPositionItem label="Project View" color={textColor} />
							<LogoPositionItem label="Share Link Sign-Up Page" color={textColor} />
						</Stack>
						<Stack spacing={10}>
							<LogoPositionItem label="Project Invite Sign-Up Page" color={textColor} />
							<LogoPositionItem label="Project Email Notification" color={textColor} />
						</Stack>
					</Group>
				</Stack>
				<Group spacing={6} position="right">
					{showCancelButton ? (
						<Button
							color="dark.9"
							variant="white"
							onClick={onCancelClick}
						>
							Cancel
						</Button>
					) : (
						<Button
							color="dark.9"
							leftIcon={<Icon icon="settings_backup_restore" size={18} />}
							variant="white"
							onClick={onRestoreDefaultClick}
							loading={isDeletingLogo}
							disabled={restoreDefaultButtonDisabled}
						>
							Restore Default
						</Button>
					)}
					<Button disabled={saveButtonDisabled} onClick={onSaveClick} loading={isSaving}>
						Save
					</Button>
				</Group>
			</Stack>
		</Paper>
	);
};

export default EditWorkspaceLogo;
